import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronLeft, Clock, Hand, Image as IconaImmagine, Lock, Send, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useMedia } from "@/hooks/use-media";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Initials } from "@/components/ui/list-row";
import { StatusBadge } from "@/components/ui/status-badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SceltaCliente from "@/components/common/SceltaCliente";
import SchedaCliente from "@/components/messaggi/SchedaCliente";
import ModelloDialog from "@/components/messaggi/ModelloDialog";
import { erroreDi, giornoDi, giornoLungo, oggi, ora } from "@/lib/crm";
import { testoDelModello } from "@/lib/modelli";
import { cn } from "@/lib/utils";

const STATI = [
  { id: "aperta", label: "Aperta" },
  { id: "in_corso", label: "In corso" },
  { id: "chiusa", label: "Chiusa" },
];
const NESSUNO = "__nessuno__";
const MOTIVI = {
  chiesto_umano: "ha chiesto di parlare con una persona",
  non_so: "una domanda a cui l'assistente non sa rispondere",
  soldi_contratti: "soldi, contratto o consegna",
  reclamo: "un problema su un lavoro",
  urgente: "sembra urgente",
  rifiuto_modello: "un messaggio che l'assistente non ha potuto gestire",
};
const SPUNTE = { accepted: "inviato", sent: "consegnato a WhatsApp", delivered: "recapitato", read: "letto", failed: "non recapitato" };

/** Quanto manca alla chiusura della finestra: «21 h 40 min», «12 min». */
function mancano(scadeAt, adesso) {
  const ms = new Date(scadeAt).getTime() - adesso;
  if (ms <= 0) return null;
  const min = Math.floor(ms / 60000);
  const h = Math.floor(min / 60);
  return h ? `${h} h ${String(min % 60).padStart(2, "0")} min` : `${min} min`;
}

/* ── Un messaggio del filo ─────────────────────────────────────────────────── */
function Bolla({ m }) {
  const mio = m.direction === "out";
  const automatico = m.tipo === "template";
  const modello = automatico ? testoDelModello(m.testo) : null;
  const apriMedia = async () => {
    /* Indirizzo firmato chiesto adesso, scade in un minuto: sono foto di case di persone. */
    try {
      const r = await api.get(`/messaggi/media/${m.id}/link`);
      window.open(r.data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      toast.error(erroreDi(err));
    }
  };
  return (
    <li className={cn("flex", mio ? "justify-end" : "justify-start")} data-testid={`messaggio-${m.wa_message_id}`}>
      <div
        className={cn(
          "grid max-w-[85%] gap-micro rounded-lg px-group py-inside text-body sm:max-w-[70%]",
          mio ? "bg-brand text-on-brand" : "border border-line bg-surface text-ink",
        )}
      >
        {automatico ? <span className="text-caption font-bold opacity-80">{modello ? "Modello WhatsApp" : "Messaggio automatico"}</span> : null}
        {m.media_path ? (
          <button type="button" onClick={apriMedia} className="inline-flex min-h-11 items-center gap-inside text-left font-bold underline underline-offset-2 hover:no-underline md:min-h-8" data-testid={`media-${m.id}`}>
            <IconaImmagine className="size-4" aria-hidden />
            {(m.media_mime || "").startsWith("image/") ? "Apri la foto" : "Apri l'allegato"}
          </button>
        ) : null}
        {m.testo ? <p className="whitespace-pre-line break-words">{modello ?? (automatico ? m.testo.replace(/^\[[^\]]+\]\s*/, "") : m.testo)}</p> : null}
        <span className="justify-self-end text-caption tabular-nums opacity-80">
          {ora(m.wa_timestamp)}
          {mio && m.status ? ` · ${SPUNTE[m.status] || m.status}` : ""}
        </span>
      </div>
    </li>
  );
}

/* Sotto il computer largo la scheda non ha una colonna: una striscia sotto la testata dice se il cliente c'è. */
function Striscia({ f, onAggiungi, onAggancia, conAggancio }) {
  if (!f.cliente)
    return (
      <div className="flex flex-wrap items-center gap-inside border-b border-line-subtle bg-warning-bg px-card py-group text-small text-warning-ink" data-testid="striscia-senza-cliente">
        <span className="font-bold">Non è in archivio.</span>
        <Button size="sm" variant="secondary" onClick={onAggiungi} data-testid="filo-aggiungi-cliente">
          Aggiungi come cliente
        </Button>
        {conAggancio ? (
          <div className="min-w-[14rem] flex-1 sm:max-w-xs">
            <SceltaCliente valore={null} onScegli={onAggancia} testId="filo-aggancia" segnaposto="Oppure collegalo a un cliente" />
          </div>
        ) : null}
      </div>
    );
  if (!f.cliente.nome) return null;
  return (
    <div className="flex flex-wrap items-center gap-inside border-b border-line-subtle bg-surface-2 px-card py-inside text-small text-ink-2">
      {f.cliente.stato_label ? <StatusBadge tone="brand">{f.cliente.stato_label}</StatusBadge> : null}
      {f.cliente.sede ? <span>{f.cliente.sede}</span> : null}
    </div>
  );
}

/* ── Il filo ───────────────────────────────────────────────────────────────── */
export default function Filo({ waId, onIndietro, onCambiato, adesso, modelloPronto, versione, onAggiungi }) {
  const { profile } = useAuth() || {};
  const largo = useMedia("(min-width: 1280px)");
  const md = useMedia("(min-width: 768px)");
  const [gestisci, setGestisci] = useState(false); // sul telefono i comandi stanno dietro «Gestisci»: al filo serve lo spazio
  const [f, setF] = useState(null);
  const [testo, setTesto] = useState("");
  const [daSuggerimento, setDaSuggerimento] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modelloAperto, setModelloAperto] = useState(false);
  const fondo = useRef(null);
  const campo = useRef(null);

  const carica = useCallback(() => {
    api
      .get(`/messaggi/${waId}`)
      .then((r) => setF(r.data))
      .catch((e) => {
        toast.error(erroreDi(e));
        onIndietro();
      });
  }, [waId, onIndietro]);

  useEffect(() => {
    setF(null);
    setTesto("");
    setDaSuggerimento(null);
    carica();
    /* Quasi in tempo reale: il filo si rilegge ogni 8 secondi finché è aperto. */
    const t = setInterval(carica, 8000);
    return () => clearInterval(t);
  }, [carica]);

  /* La pagina chiede di rileggere subito (cliente appena aggiunto). */
  useEffect(() => {
    if (versione) carica();
  }, [versione]); // eslint-disable-line react-hooks/exhaustive-deps

  const nMessaggi = f?.messaggi?.length || 0;
  useEffect(() => {
    fondo.current?.scrollIntoView?.({ block: "end" });
  }, [nMessaggi]);

  const per = useMemo(() => {
    const gruppi = [];
    for (const m of f?.messaggi || []) {
      const g = giornoDi(m.wa_timestamp);
      if (!gruppi.length || gruppi[gruppi.length - 1].giorno !== g) gruppi.push({ giorno: g, messaggi: [] });
      gruppi[gruppi.length - 1].messaggi.push(m);
    }
    return gruppi;
  }, [f]);

  const manda = async (contenuto, suggerimentoId) => {
    setBusy(true);
    try {
      const r = await api.post(`/messaggi/${waId}/rispondi`, { testo: contenuto, suggerimento_id: suggerimentoId || null });
      if (r.data.stato === "saltato" || r.data.stato === "errore") toast.error(`Non è partito: ${r.data.motivo_salto || "errore di invio"}`);
      else toast.success(r.data.stato === "inviato" ? "Inviato" : "In invio");
      setTesto("");
      setDaSuggerimento(null);
      carica();
      onCambiato();
    } catch (err) {
      toast.error(erroreDi(err));
    } finally {
      setBusy(false);
    }
  };

  const scarta = async (id) => {
    try {
      await api.post(`/messaggi/suggerimenti/${id}/scarta`);
      toast.success("Proposta scartata");
      if (daSuggerimento === id) {
        setDaSuggerimento(null);
        setTesto("");
      }
      carica();
      onCambiato();
    } catch (err) {
      toast.error(erroreDi(err));
    }
  };

  const modifica = async (dati, msg) => {
    try {
      await api.patch(`/messaggi/${waId}`, dati);
      if (msg) toast.success(msg);
      carica();
      onCambiato();
    } catch (err) {
      toast.error(erroreDi(err));
    }
  };

  const chiudiEscalation = async (id) => {
    try {
      await api.post(`/messaggi/escalation/${id}/chiudi`);
      carica();
      onCambiato();
    } catch (err) {
      toast.error(erroreDi(err));
    }
  };

  if (!f) {
    return (
      <div className="grid gap-group p-card" aria-busy="true" aria-label="Caricamento conversazione">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-16 w-2/3" />
        <Skeleton className="ml-auto h-16 w-2/3" />
      </div>
    );
  }

  const c = f.contatto;
  const aperta = f.finestra.aperta;
  const resta = aperta ? mancano(f.finestra.scade_at, adesso) : null;
  const aggancia = (cl) => modifica({ cliente_id: cl.id }, `Agganciata a ${cl.nome}`);

  const chat = (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {/* Chi è, e cosa ci si fa */}
      <header className="grid min-w-0 grid-cols-1 gap-group border-b border-line-subtle px-card py-group">
        <div className="flex items-center gap-inside">
          <Button variant="ghost" size="icon" className="-ml-inside shrink-0 lg:hidden" onClick={onIndietro} aria-label="Torna alle conversazioni">
            <ChevronLeft />
          </Button>
          <Initials name={c.nome} />
          <div className="min-w-0 flex-1">
            <h2 className="truncate font-bold text-ink-strong" data-testid="filo-nome">{c.nome}</h2>
            <p className="truncate text-small tabular-nums text-ink-2">
              +{c.wa_id} · {c.segue ? `Segue ${c.segue}` : "Senza nessuno"}
            </p>
          </div>
          {!largo && f.cliente?.nome ? (
            <Button asChild variant="secondary" size="sm" className="shrink-0">
              <Link to={`/clienti/${f.cliente.id}`} data-testid="filo-scheda">
                Scheda
              </Link>
            </Button>
          ) : null}
          {!md ? (
            <Button variant="secondary" size="sm" className="shrink-0" aria-expanded={gestisci} onClick={() => setGestisci((v) => !v)} data-testid="filo-gestisci">
              Gestisci
            </Button>
          ) : null}
        </div>
        <div className={cn("grid grid-cols-[7.5rem_minmax(0,1fr)] gap-inside sm:flex sm:flex-wrap sm:items-center", !md && !gestisci && "hidden")}>
          <Select value={c.stato} onValueChange={(v) => modifica({ stato: v })}>
            <SelectTrigger className="sm:w-36" aria-label="Stato della conversazione" data-testid="filo-stato">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATI.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {f.posso_affidare ? (
            <Select
              value={c.assigned_to || NESSUNO}
              onValueChange={(v) => modifica(v === NESSUNO ? { togli_assegnazione: true } : { assigned_to: v }, "Conversazione affidata")}
            >
              <SelectTrigger className="sm:w-52" aria-label="Chi la segue" data-testid="filo-segue">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NESSUNO}>Senza nessuno</SelectItem>
                {f.persone.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <span className="self-center text-small text-ink-2">{c.segue ? `La segue ${c.segue}` : "Senza nessuno"}</span>
          )}
          <Button
            variant="secondary"
            className="col-span-2 sm:col-span-1"
            onClick={() => modifica({ prendi_in_carico: !c.presa_in_carico_at }, c.presa_in_carico_at ? "L'assistente può rispondere di nuovo" : "L'assistente tace su questa conversazione")}
            aria-pressed={Boolean(c.presa_in_carico_at)}
            data-testid="filo-prendi"
          >
            <Hand />
            {c.presa_in_carico_at ? "Rilascia all'assistente" : "Prendo io"}
          </Button>
          {!md && !f.cliente ? (
            <div className="col-span-2">
              <SceltaCliente valore={null} onScegli={aggancia} testId="filo-aggancia" segnaposto="Oppure collegalo a un cliente" />
            </div>
          ) : null}
        </div>
      </header>

      {!largo ? <Striscia f={f} onAggiungi={() => onAggiungi(f)} onAggancia={aggancia} conAggancio={md} /> : null}

      {/* L'assistente ha passato la palla */}
      {f.escalation.map((e) => (
        <div key={e.id} role="status" className="flex items-start gap-group border-b border-line-subtle bg-warning-bg px-card py-group text-warning-ink" data-testid="filo-escalation">
          <AlertTriangle className="mt-micro size-4 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1 text-body">
            <p className="font-bold">L'assistente l'ha passata a una persona: {MOTIVI[e.motivo] || e.motivo}</p>
            {e.riassunto ? <p className="mt-micro">{e.riassunto}</p> : null}
          </div>
          <Button size="sm" variant="secondary" className="shrink-0" onClick={() => chiudiEscalation(e.id)} data-testid="filo-escalation-chiudi">
            Fatto
          </Button>
        </div>
      ))}

      {/* Il filo */}
      <div className="min-h-0 flex-1 overflow-y-auto bg-surface-2 px-card py-group" data-testid="filo-messaggi">
        {per.length ? (
          <div className="grid gap-card">
            {per.map((g) => (
              <section key={g.giorno} className="grid gap-inside" aria-label={giornoLungo(g.giorno)}>
                <p className="justify-self-center text-caption text-ink-2 first-letter:uppercase">{g.giorno === oggi() ? "oggi" : giornoLungo(g.giorno)}</p>
                <ul className="grid gap-inside">
                  {g.messaggi.map((m) => (
                    <Bolla key={m.id} m={m} />
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : (
          <p className="text-body text-ink-2">Nessun messaggio ancora.</p>
        )}

        {f.in_coda.length ? (
          <ul className="mt-group grid gap-inside">
            {f.in_coda.map((r) => (
              <li key={r.id} className="flex justify-end">
                <div className={cn("grid max-w-[85%] gap-micro rounded-lg border border-dashed px-group py-inside text-body sm:max-w-[70%]", r.stato === "in_attesa" ? "border-line-input text-ink-2" : "border-danger text-danger-ink")}>
                  <p className="whitespace-pre-line break-words">{r.testo}</p>
                  <span className="justify-self-end text-caption">{r.stato === "in_attesa" ? "in invio…" : `non partito: ${r.motivo_salto || "errore di invio"}`}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        {/* Le proposte dell'assistente: nel filo, dove si decide */}
        {f.suggerimenti.length ? (
          <div className="mt-card grid gap-group">
            {f.suggerimenti.map((s) => (
              <div key={s.id} className="grid gap-inside rounded-md border border-brand-soft bg-surface p-group" data-testid={`suggerimento-${s.id}`}>
                <p className="flex items-center gap-inside text-caption font-bold text-brand-ink">
                  <Sparkles className="size-3.5" aria-hidden />
                  Proposta dell'assistente
                </p>
                <p className="whitespace-pre-line text-body text-ink">{s.proposta}</p>
                <div className="mt-micro flex flex-wrap gap-inside">
                  <Button size="sm" disabled={busy || !aperta} onClick={() => manda(s.proposta, s.id)} data-testid={`suggerimento-invia-${s.id}`}>
                    <Send />
                    Invia
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!aperta}
                    onClick={() => {
                      setTesto(s.proposta);
                      setDaSuggerimento(s.id);
                      campo.current?.focus();
                    }}
                    data-testid={`suggerimento-modifica-${s.id}`}
                  >
                    Modifica
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => scarta(s.id)} data-testid={`suggerimento-scarta-${s.id}`}>
                    Scarta
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
        <div ref={fondo} />
      </div>

      {/* Scrivere: testo libero solo dentro la finestra delle 24 ore, un modello fuori */}
      <footer className="border-t border-line-subtle bg-surface px-card py-group">
        {aperta ? (
          <form
            className="grid gap-inside"
            onSubmit={(e) => {
              e.preventDefault();
              if (testo.trim()) manda(testo.trim(), daSuggerimento);
            }}
          >
            <p className="flex items-center gap-1.5 text-small text-ink-2" data-testid="filo-finestra">
              <Clock className="size-3.5" aria-hidden />
              Puoi scrivere liberamente ancora per {resta || "pochi minuti"}
              {daSuggerimento ? <span className="text-brand-ink"> · stai modificando la proposta dell'assistente</span> : null}
            </p>
            <div className="flex items-end gap-inside">
              <Textarea
                ref={campo}
                rows={2}
                value={testo}
                onChange={(e) => setTesto(e.target.value)}
                placeholder={`Scrivi a ${c.nome}`}
                aria-label="Risposta"
                className="min-h-11 resize-none"
                data-testid="filo-testo"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && testo.trim()) manda(testo.trim(), daSuggerimento);
                }}
              />
              <Button type="submit" size="icon" className="shrink-0" disabled={busy || !testo.trim()} aria-label="Invia" data-testid="filo-invia">
                <Send />
              </Button>
            </div>
          </form>
        ) : (
          <div className="grid gap-group" data-testid="filo-finestra-chiusa">
            <div className="flex items-start gap-group rounded-md bg-warning-bg px-group py-group text-body text-warning-ink">
              <Lock className="mt-micro size-4 shrink-0" aria-hidden />
              <p>
                <span className="font-bold">Finestra chiusa.</span>{" "}
                {f.finestra.scade_at
                  ? "Sono passate più di 24 ore dall'ultimo messaggio del cliente: WhatsApp lascia mandare solo un messaggio già approvato. Quando risponde, si riapre."
                  : "Il cliente non ha ancora scritto a questo numero: WhatsApp lascia mandare solo un messaggio già approvato. Quando risponde, si apre la finestra."}
              </p>
            </div>
            {modelloPronto ? (
              <Button variant="secondary" onClick={() => setModelloAperto(true)} data-testid="filo-modello">
                Manda un modello
              </Button>
            ) : null}
          </div>
        )}
      </footer>
      <ModelloDialog
        open={modelloAperto}
        onOpenChange={setModelloAperto}
        waId={waId}
        nomeContatto={c.nome}
        mittente={(profile?.nome || "").trim()}
        onFatto={() => {
          carica();
          onCambiato();
        }}
      />
    </div>
  );

  return (
    <div className={cn("h-full min-h-0 min-w-0", largo && "grid grid-cols-[minmax(0,1fr)_18rem]")} data-testid="filo">
      {chat}
      {largo ? (
        <aside className="min-h-0 overflow-y-auto border-l border-line-subtle bg-surface p-card" aria-label="Scheda del cliente">
          <SchedaCliente filo={f} onAggiungi={() => onAggiungi(f)} onAggancia={aggancia} />
        </aside>
      ) : null}
    </div>
  );
}
